import { useCampaignStore } from '@/stores/campaignStore';
import { useLeafletingStore } from '@/stores/leafletingStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface BackgroundFetchOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
  onLoadingChange?: (loading: boolean) => void;
}

// Create a hook that subscribes to store data and handles background fetching
export const useBackgroundFetch = <T>(
  data: T,
  fetchFunction: () => Promise<T>,
  dependencies: any[] = [],
  options: BackgroundFetchOptions = {},
  contextCheck?: () => boolean
) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const updateLoading = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
    options.onLoadingChange?.(isLoading);
  }, [options]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    // If we have cached data (not null), show it immediately and start background fetch
    if (data !== null && data !== undefined && !forceRefresh) {
      updateLoading(false);
      options.onSuccess?.(data);
      
      // Start background fetch for fresh data (don't await)
      fetchFunction().then((freshData) => {
        setError(null);
        setLastFetchTime(Date.now());
        options.onSuccess?.(freshData);
      }).catch((err) => {
        const error = err instanceof Error ? err : new Error('Unknown error');
        console.warn('Background fetch failed, using cached data:', error);
        // Don't set error state if we have cached data
      });
      
      return;
    }
    
    // No cached data or force refresh, fetch from network
    try {
      updateLoading(true);
      const freshData = await fetchFunction();
      setError(null);
      setLastFetchTime(Date.now());
      options.onSuccess?.(freshData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      options.onError?.(error);
    } finally {
      updateLoading(false);
    }
  }, [fetchFunction, data, options, updateLoading]);

  useEffect(() => {
    // Only fetch if contextCheck passes
    if (contextCheck && !contextCheck()) {
      updateLoading(false); // Ensure loading is false if context is not met
      return;
    }
    fetchData();
  }, dependencies);

  // Create a refetch function that forces a fresh fetch
  const refetch = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
    lastFetchTime,
  };
};

const getCampaignData = (branchId: string, campaignId: string, cachedData: any) => {
  if (!branchId || !campaignId) return null;
  return cachedData[branchId]?.campaigns?.[campaignId] || null;
};

// Campaign store wrapper for useBackgroundFetch that handles context checking and store synchronization
export const useCampaignStoreFetch = <T>(
  dataSelector: (campaignData: any) => T,
  fetchFunction: () => Promise<T>,
  additionalDependencies: any[] = [],
  options: BackgroundFetchOptions = {}
) => {
  const selectedBranch = useNavigationStore(state => state.getSelectedBranch()?.branch_short_name);
  const selectedCampaign = useNavigationStore(state => state.getSelectedCampaign()?.boundary_id);
  
  // Handle store synchronization when context changes
  useEffect(() => {
    if (selectedBranch && selectedCampaign) {
      useCampaignStore.getState().syncWithNavigationStore();
    } else {
      useCampaignStore.getState().clearCurrentContext();
    }
  }, [selectedBranch, selectedCampaign]);
  
  // Get data from campaign store using the selector with context
  const data = useCampaignStore(state => 
    selectedBranch && selectedCampaign 
      ? dataSelector(getCampaignData(selectedBranch, selectedCampaign, state.cachedData))
      : null
  );
  
  // Context check: ensure both branch and campaign are selected
  const contextCheck = () => !!(selectedBranch && selectedCampaign);
  
  // Combine context dependencies with additional dependencies
  const allDependencies = [selectedBranch, selectedCampaign, ...additionalDependencies];
  
  return useBackgroundFetch(
    data,
    fetchFunction,
    allDependencies,
    options,
    contextCheck
  );
};

// Specific hooks for common use cases
export const useBackgroundBattlePlans = () => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.battlePlans || null,
    () => useCampaignStore.getState().fetchBattlePlans()
  );
};

export const useBackgroundEngagements = (battlePlanId: string, filterByElectoralDivisionIds: boolean = false) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.engagements?.[battlePlanId] || null,
    () => useCampaignStore.getState().fetchEngagements(battlePlanId, filterByElectoralDivisionIds),
    [battlePlanId, filterByElectoralDivisionIds]
  );
};

export const useBackgroundPollingStations = (battlePlanId: string) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.pollingStations?.[battlePlanId] || null,
    () => useCampaignStore.getState().fetchPollingStations(battlePlanId),
    [battlePlanId]
  );
};

export const useBackgroundAddresses = (engagementId: string, forceRefresh?: number) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.addresses?.[engagementId] || null,
    () => useCampaignStore.getState().fetchAddressesForEngagement(engagementId, undefined, undefined),
    [engagementId, forceRefresh]
  );
};

export const useBackgroundStreetBreakdown = (engagementId: string, forceRefresh?: number) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.streetBreakdowns?.[engagementId] || null,
    () => useCampaignStore.getState().fetchEngagementStreetBreakdown(engagementId),
    [engagementId, forceRefresh]
  );
};

export const useBackgroundPostcodeSlicePolygons = (engagementId: string, forceRefresh?: number) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.postcodeSlicePolygons?.[engagementId] || null,
    () => useCampaignStore.getState().fetchPostcodeSlicePolygons(engagementId),
    [engagementId, forceRefresh]
  );
};

export const useBackgroundEngagementMapDetails = (engagementId: string, battlePlanId: string) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.engagementMapDetails?.[engagementId] || null,
    async () => {
      const result = await useCampaignStore.getState().fetchEngagementMapDetails([engagementId], battlePlanId);
      return result[engagementId] || null;
    },
    [engagementId, battlePlanId]
  );
};

export const useBackgroundMyActiveEngagements = () => {
  const selectedBranch = useNavigationStore(state => state.getSelectedBranch()?.branch_short_name);
  
  // Handle store synchronization when context changes
  useEffect(() => {
    if (selectedBranch) {
      useCampaignStore.getState().syncWithNavigationStore();
    } else {
      useCampaignStore.getState().clearCurrentContext();
    }
  }, [selectedBranch]);
  
  const myActiveEngagements = useCampaignStore(state => 
    selectedBranch 
      ? state.cachedData[selectedBranch]?.myActiveEngagements || null
      : null
  );
  
  // Context check: ensure branch is selected (campaign is not required for active engagements)
  const contextCheck = () => !!selectedBranch;
  
  return useBackgroundFetch(
    myActiveEngagements,
    () => useCampaignStore.getState().fetchMyActiveEngagements(),
    [selectedBranch],
    {},
    contextCheck
  );
};

export const useBackgroundCampaigns = (branchId: string) => {
  const store = useNavigationStore.getState();
  const campaigns = store.getCampaigns(branchId);
  
  return useBackgroundFetch(
    campaigns,
    () => store.fetchCampaigns(branchId),
    [branchId]
  );
};

export const useBackgroundOrbat = (branchId: string) => {
  const store = useNavigationStore.getState();
  const orbat = store.getOrbat(branchId);
  
  return useBackgroundFetch(
    orbat,
    () => store.fetchOrbat(branchId),
    [branchId]
  );
};

export const useBackgroundBranches = () => {
  const store = useNavigationStore.getState();
  const branches = store.branches;
  
  return useBackgroundFetch(
    branches,
    () => store.fetchBranches(),
    []
  );
};

export const useBackgroundTellingVotersByEngagements = (engagementIds: string[] | null) => {
  return useCampaignStoreFetch(
    (campaignData) => {
      const engagementIdsKey = engagementIds?.sort().join(',');
      return engagementIdsKey ? campaignData?.tellingVotersByEngagements?.[engagementIdsKey]?.voters : null;
    },
    () => useCampaignStore.getState().fetchTellingVotersByEngagements(engagementIds),
    [engagementIds?.sort().join(',')] // Use sorted string as dependency
  );
};

// Leafleting hooks - using a custom wrapper similar to useCampaignStoreFetch but for leafleting store
export const useLeafletingStoreFetch = <T>(
  dataSelector: (leafletingData: any) => T,
  fetchFunction: () => Promise<T>,
  additionalDependencies: any[] = [],
  options: BackgroundFetchOptions = {}
) => {
  const selectedBranch = useNavigationStore(state => state.getSelectedBranch()?.branch_short_name);
  const selectedCampaign = useNavigationStore(state => state.getSelectedCampaign()?.boundary_id);
  
  // Handle store synchronization when context changes
  useEffect(() => {
    if (selectedBranch && selectedCampaign) {
      useLeafletingStore.getState().syncWithNavigationStore();
    } else {
      useLeafletingStore.getState().clearCurrentContext();
    }
  }, [selectedBranch, selectedCampaign]);
  
  // Get data from leafleting store using the selector with context
  const data = useLeafletingStore((state: any) => {
    if (!selectedBranch || !selectedCampaign) return null;
    const leafletingData = state.cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
    return leafletingData ? dataSelector(leafletingData) : null;
  });
  
  // Context check: ensure both branch and campaign are selected
  const contextCheck = () => !!(selectedBranch && selectedCampaign);
  
  // Combine context dependencies with additional dependencies
  const allDependencies = [selectedBranch, selectedCampaign, ...additionalDependencies];
  
  return useBackgroundFetch(
    data,
    fetchFunction,
    allDependencies,
    options,
    contextCheck
  );
};

export const useBackgroundLeafletingRounds = () => {
  return useLeafletingStoreFetch(
    (leafletingData) => leafletingData?.rounds || null,
    () => useLeafletingStore.getState().fetchRounds()
  );
};

export const useBackgroundLeafletingRoutes = (roundId: string) => {
  return useLeafletingStoreFetch(
    (leafletingData) => leafletingData?.routes?.[roundId] || null,
    () => useLeafletingStore.getState().fetchRoutes(roundId),
    [roundId]
  );
};

export const useBackgroundMyActiveLeafletingRoutes = () => {
  const selectedBranch = useNavigationStore(state => state.getSelectedBranch()?.branch_short_name);
  
  // Handle store synchronization when context changes
  useEffect(() => {
    if (selectedBranch) {
      useLeafletingStore.getState().syncWithNavigationStore();
    } else {
      useLeafletingStore.getState().clearCurrentContext();
    }
  }, [selectedBranch]);
  
  // Get data from leafleting store using the selector with context
  const data = useLeafletingStore((state: any) => {
    if (!selectedBranch) return null;
    return state.cachedData[selectedBranch]?.myActiveRoutes || null;
  });
  
  // Context check: ensure branch is selected
  const contextCheck = () => !!selectedBranch;
  
  return useBackgroundFetch(
    data,
    () => useLeafletingStore.getState().fetchMyActiveRoutes(),
    [selectedBranch],
    {},
    contextCheck
  );
};

export const useBackgroundLeafletingGeometry = (roundId: string, assignmentIds: string[]) => {
  // Create a stable string key for assignment IDs to prevent unnecessary re-fetches
  const assignmentIdsKey = useMemo(() => {
    return assignmentIds.sort().join(',');
  }, [assignmentIds]);

  return useLeafletingStoreFetch(
    (leafletingData) => {
      // Check if we have assignmentMapDetails and all expected assignment IDs are present
      if (!leafletingData?.assignmentMapDetails) {
        return null;
      }
      
      // Check that all expected assignment IDs are present
      const hasAllAssignments = assignmentIds.every(id => 
        leafletingData.assignmentMapDetails[id]
      );
      
      return hasAllAssignments ? leafletingData.assignmentMapDetails : null;
    },
    () => {
      const store = useLeafletingStore.getState();
      
      // Check which assignment IDs are already cached
      const missingAssignmentIds: string[] = [];
      
      assignmentIds.forEach(assignmentId => {
        const cached = store.getAssignmentMapDetails(assignmentId);
        if (!cached) {
          missingAssignmentIds.push(assignmentId);
        }
      });
      
      // Fetch missing ones (fetchAssignmentMapDetails handles empty array case)
      return store.fetchAssignmentMapDetails(missingAssignmentIds).then(newDetails => {
        // Get all requested data (cached + newly fetched)
        const allDetails: { [assignmentId: string]: any } = {};
        
        assignmentIds.forEach(assignmentId => {
          const cached = store.getAssignmentMapDetails(assignmentId);
          if (cached) {
            allDetails[assignmentId] = cached;
          }
        });
        
        // Add newly fetched data
        Object.entries(newDetails).forEach(([assignmentId, details]) => {
          allDetails[assignmentId] = details;
        });
        
        return allDetails;
      });
    },
    [roundId, assignmentIdsKey]
  );
};

export const useBackgroundRouteSummary = (assignmentId: string) => {
  return useLeafletingStoreFetch(
    (leafletingData) => leafletingData?.routeSummaries?.[assignmentId] || null,
    () => useLeafletingStore.getState().fetchRouteSummary(assignmentId),
    [assignmentId]
  );
};

export const useBackgroundPollingStationInfo = (engagementId: string) => {
  return useCampaignStoreFetch(
    (campaignData) => campaignData?.pollingStationInfo?.[engagementId] || null,
    () => useCampaignStore.getState().fetchPollingStationInfo(engagementId),
    [engagementId]
  );
};

export const useBackgroundVIStats = () => {
  const selectedBranch = useNavigationStore(state => state.getSelectedBranch());
  const selectedCampaign = useNavigationStore(state => state.getSelectedCampaign()?.boundary_id);
  
  // Check if user is a branch officer (Commander, Lieutenant, or Sgt Major)
  const isBranchOfficer = selectedBranch?.branch_role && 
    ['commander', 'lieutenant', 'sgt major'].includes(selectedBranch.branch_role.toLowerCase());
  
  // Handle store synchronization when context changes
  useEffect(() => {
    if (selectedBranch?.branch_short_name && selectedCampaign) {
      useCampaignStore.getState().syncWithNavigationStore();
    } else {
      useCampaignStore.getState().clearCurrentContext();
    }
  }, [selectedBranch?.branch_short_name, selectedCampaign]);
  
  // Get data from campaign store using the selector with context
  const data = useCampaignStore(state => 
    selectedBranch?.branch_short_name && selectedCampaign 
      ? getCampaignData(selectedBranch.branch_short_name, selectedCampaign, state.cachedData)?.viStats || null
      : null
  );
  
  // Context check: ensure both branch and campaign are selected AND user is a branch officer
  const contextCheck = () => !!(selectedBranch?.branch_short_name && selectedCampaign && isBranchOfficer);
  
  // Combine context dependencies
  const allDependencies = [selectedBranch?.branch_short_name, selectedCampaign, isBranchOfficer];
  
  return useBackgroundFetch(
    data,
    () => useCampaignStore.getState().fetchVIStats(),
    allDependencies,
    {},
    contextCheck
  );
};
